import { NormalizedColumn, ResponsiveResolvedField } from './types';
import { resolveFieldValue } from './resolveFieldValue';

export function buildResolvedFields<TData, TColId extends string>(
    columns: NormalizedColumn<TData, TColId>[],
    row: TData,
    rowIndex: number,
    layoutSlots?: Partial<Record<TColId, string>>,
    layoutHidden?: readonly TColId[],
): ResponsiveResolvedField<TData, TColId>[] {
    return columns.map((col) => {
        const { rawValue, displayValue } = resolveFieldValue(col.colDef, row, rowIndex);
        const layoutSlot = layoutSlots?.[col.colId];
        const isLayoutHidden = layoutHidden?.includes(col.colId) ?? false;

        return {
            colId: col.colId,
            field: col.field as keyof TData | undefined,
            headerName: col.headerName,
            rawValue,
            displayValue,
            sortable: col.sortable,
            filterable: col.filterable,
            hidden: col.hidden || isLayoutHidden,
            slot: (layoutSlot ?? col.slot) as ResponsiveResolvedField<TData, TColId>['slot'],
            order: col.order,
            emphasis: col.emphasis,
            colDef: col.colDef,
        };
    });
}

export function sortFields<TData, TColId extends string>(
    fields: ResponsiveResolvedField<TData, TColId>[],
    layoutOrder?: readonly TColId[],
): ResponsiveResolvedField<TData, TColId>[] {
    if (layoutOrder && layoutOrder.length > 0) {
        const orderMap = new Map(layoutOrder.map((id, idx) => [id, idx]));
        return [...fields].sort((a, b) => {
            const aOrder = orderMap.get(a.colId) ?? 9999;
            const bOrder = orderMap.get(b.colId) ?? 9999;
            return aOrder - bOrder;
        });
    }

    // Fall back to mobile.order, then original column order
    return [...fields].sort((a, b) => {
        const aOrder = a.order ?? 9999;
        const bOrder = b.order ?? 9999;
        return aOrder - bOrder;
    });
}
